import { useState } from 'react'
import type { ResumoGmud, StatusGmud } from '../../../types/gmud'
import type { LigacoesDoArraste, PropostaDeArraste } from './arraste'
import { BarraDeSalto } from './BarraDeSalto'
import { COLUNAS_DO_QUADRO, agruparPorStatus } from './colunasDoQuadro'
import { ColunaDoQuadro } from './ColunaDoQuadro'
import { QUADRO } from './textosDoQuadro'

interface Props {
  itens: ResumoGmud[]
  /** Escreve na região `aria-live` da página. */
  aoAnunciar: (texto: string) => void
}

/**
 * As sete colunas, e o estado do arraste.
 *
 * **Soltar um cartão não executa nada: vira uma PROPOSTA.** O quadro guarda "este cartão foi solto
 * nesta zona" e o cartão abre o seu próprio menu com esse destino; quem traduz destino em comando é
 * a resposta do servidor, e quem executa é o mesmo hook do botão do detalhe. Não existe caminho
 * paralelo porque não existe segundo código — existe um gesto a mais chegando ao mesmo lugar.
 *
 * **O cartão não se move enquanto o servidor não confirma.** Nada de otimismo: 12 das 18 transições
 * exigem justificativa escrita, então cancelar o diálogo é o caso comum, e um cartão já mudado de
 * coluna teria de voltar sozinho — pior que nunca ter saído.
 *
 * @param props os cartões da consulta e a região de anúncios
 * @returns o quadro
 */
export function Quadro({ itens, aoAnunciar }: Props) {
  const [emArraste, setEmArraste] = useState<number | null>(null)
  const [proposta, setProposta] = useState<PropostaDeArraste | null>(null)
  const porStatus = agruparPorStatus(itens)

  const ligacoes: LigacoesDoArraste = {
    emArraste,
    proposta,
    aoPegar: (gmud) => setEmArraste(gmud.id),
    aoLargar: () => setEmArraste(null),
    aoSoltarNaZona: (destino: StatusGmud, nomeDaZona: string) => {
      if (emArraste === null) {
        return
      }
      setProposta({ gmudId: emArraste, destino, nomeDaZona })
      setEmArraste(null)
    },
    aoFecharProposta: () => setProposta(null),
    aoAnunciar,
  }

  return (
    <div className="space-y-3">
      <BarraDeSalto porStatus={porStatus} />
      <p className="max-w-prose text-xs text-slate-600">{QUADRO.arrasteAjuda}</p>

      {/* A rolagem lateral fica NESTA caixa, nunca na página: uma faixa larga que empurra o
          `<body>` faz o menu e o cabeçalho andarem junto, e a tela inteira parece torta. */}
      <div className="overflow-x-auto pb-2">
        <ol className="flex items-stretch gap-3">
          {COLUNAS_DO_QUADRO.map((coluna) => (
            <ColunaDoQuadro
              key={coluna.etapa.numero}
              coluna={coluna}
              porStatus={porStatus}
              ligacoes={ligacoes}
            />
          ))}
        </ol>
      </div>
    </div>
  )
}
